/* CUSTOM: Size finder recommendation on the product page — marks and preselects the size from localStorage "mm-size-profile", fills the hint or prompt line, and mirrors it into the sticky bar and quick-buy modal.
   Loaded PDP-only from snippets/custom-global-head-assets.liquid; markup from snippets/custom-product-size-finder-hint.liquid and snippets/custom-product-sticky-size.liquid. */
(() => {
  if (window.__customSizeFinderInit) return;
  window.__customSizeFinderInit = true;

  const PROFILE_KEY = 'mm-size-profile';
  const HINT_SELECTOR = '.custom-size-finder-hint';
  const PROMPT_SELECTOR = '.custom-size-finder-prompt';
  const PICKER_SELECTOR = 'variant-picker[context="main_product"]';
  const MARKER_CLASS = 'custom-size-finder-recommended';
  // The theme's size guide trigger inside the variant picker (opens x-modal[id^="size-chart-"]).
  const SIZE_GUIDE_TRIGGER_SELECTOR = 'button.link[aria-controls^="size-chart"]';
  // Sticky add-to-cart bar (snippets/custom-product-sticky-size.liquid, mounted by custom-product.js): its sheet
  // mirrors the recommendation and hands off to the theme's size guide.
  const STICKY_SELECTOR = '.custom-sticky-size';
  const STICKY_SHEET_SELECTOR = 'x-popover.custom-sticky-size__sheet';
  const STICKY_GUIDE_SELECTOR = '[data-mm-sticky-guide]';
  // Result button of the quiz (page content): a link to the collection on the size-finder page, "Select size M"
  // inside the product page's size-guide modal. Label template in data-mm-cta-select.
  const FINDER_MODAL_SELECTOR = 'x-modal[id^="size-chart-"]';
  const FINDER_CTA_SELECTOR = `${FINDER_MODAL_SELECTOR} [data-mm-cta]`;

  // One-shot preselect guard per product handle, plus the places where the customer chose a size
  // manually (never auto-preselect over a manual choice); both reset on a fresh quiz result.
  const preselectedHandles = new Set();
  const touchedScopes = new Set();

  // The manual-choice guard is keyed by PLACE, not by product: a color switch swaps the main product
  // in place (sibling product, new handle) and must not undo a size the customer picked.
  const scopeOf = (hint) => {
    const modal = hint.closest('quick-buy-modal');
    return modal ? `quick-buy:${modal.id}` : 'main';
  };

  // Signature of the profile we last acted on, so we only re-preselect on a genuinely new quiz
  // result (not when the size guide is merely opened and closed without a new answer).
  let lastProfileRaw = null;

  const norm = (value) => String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();

  const eachHint = (fn) => document.querySelectorAll(HINT_SELECTOR).forEach(fn);

  const eachPrompt = (fn) => document.querySelectorAll(PROMPT_SELECTOR).forEach(fn);

  // Each hint/prompt owns its picker: the quick-buy modal's own picker inside the modal,
  // otherwise the main product picker (the open modal is moved to body; closest() still works).
  const getPickerFor = (el) => {
    const modal = el.closest('quick-buy-modal');
    if (modal) return modal.querySelector('variant-picker[context="quick_buy"]');
    return document.querySelector(PICKER_SELECTOR);
  };

  const readProfileRaw = () => {
    try {
      return localStorage.getItem(PROFILE_KEY);
    } catch (error) {
      return null;
    }
  };

  // Target size (raw display value) for a hint's product type, or '' when no valid profile.
  const readTargetSize = (key) => {
    const raw = readProfileRaw();
    if (!raw) return '';

    let profile;
    try {
      profile = JSON.parse(raw);
    } catch (error) {
      return '';
    }
    if (!profile || profile.v !== 1 || !profile.result) return '';

    const result = profile.result;
    // 'set' (Bikini Set) uses the common swim size — no split display.
    const map = {
      top: result.top,
      bottom: result.bottom,
      swim: result.swim,
      resort: result.resort,
      onesize: result.onesize,
      set: result.swim,
    };
    const value = map[key];
    return value ? String(value) : '';
  };

  // Locate the recommended size radio + label, scoped to the size option position (color swatches
  // are never touched). Works for hint and prompt alike — both carry the same data attributes.
  const findRecommended = (hint, picker) => {
    const position = hint.dataset.mmSizeOptionPosition;
    if (!position) return null;

    const target = norm(readTargetSize(hint.dataset.mmSizeKey));
    if (!target) return null;

    const formId = hint.dataset.mmFormId;
    let selector = `input[data-option-position="${position}"]`;
    if (formId) selector += `[form="${formId}"]`;

    const radios = picker.querySelectorAll(selector);
    for (const radio of radios) {
      const label = radio.labels && radio.labels[0];
      if (!label) continue;
      if (norm(label.textContent) === target) return { radio, label };
    }
    return null;
  };

  // A programmatic radio.click() outside the topmost open overlay counts as an outside click for its
  // focus trap and closes it, so the preselect waits until that overlay is closed (see dialog:after-hide).
  const preselectBlockedByOverlay = (radio) => {
    const overlays = document.querySelectorAll('x-modal[open], quick-buy-modal[open], cart-drawer[open], x-popover[open]');
    const topmost = overlays.length ? overlays[overlays.length - 1] : null;
    return !!(topmost && !topmost.contains(radio));
  };

  // Label of the size currently selected in this hint's form. A sheet radio shares name and value with
  // its picker radio, so the picker's label is the clean size text whichever of the two was clicked.
  const selectedSizeLabel = (hint) => {
    const picker = getPickerFor(hint);
    const form = document.forms[hint.dataset.mmFormId || ''];
    const position = hint.dataset.mmSizeOptionPosition;
    if (!picker || !form || !position) return '';

    const checked = Array.from(form.elements).find(
      (el) => el.matches && el.matches(`input[data-option-position="${position}"]:checked`)
    );
    if (!checked) return '';

    const pickerRadio = picker.querySelector(
      `input[data-option-position="${position}"][value="${checked.value}"]`
    );
    const label = pickerRadio && pickerRadio.labels && pickerRadio.labels[0];
    return label ? norm(label.textContent) : '';
  };

  // A manual choice must survive a color switch: the color tiles link to the sibling product, so the page
  // reloads and the in-memory guards are gone. Session-scoped, keyed by size type (top, bottom, swim, …).
  const TOUCHED_SIZE_KEY = 'mm-size-touched';

  const readTouchedSizes = () => {
    try {
      return JSON.parse(sessionStorage.getItem(TOUCHED_SIZE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const rememberTouchedSize = (hint) => {
    const key = hint.dataset.mmSizeKey;
    const size = selectedSizeLabel(hint);
    if (!key || !size) return;
    try {
      const sizes = readTouchedSizes();
      sizes[key] = size;
      sessionStorage.setItem(TOUCHED_SIZE_KEY, JSON.stringify(sizes));
    } catch (error) {
      /* no memory, no problem */
    }
  };

  const forgetTouchedSizes = () => {
    try {
      sessionStorage.removeItem(TOUCHED_SIZE_KEY);
    } catch (error) {
      /* no memory, no problem */
    }
  };

  // True when an auto-preselect for this hit is still outstanding (available, not yet selected,
  // no blocking guard). Used both to trigger the click and to keep the hint hidden until done.
  const isPreselectPending = (hint, hit) => {
    const handle = hint.dataset.mmProductHandle || '';
    if (preselectedHandles.has(handle) || touchedScopes.has(scopeOf(hint))) return false;

    // The customer picked this size type by hand in this session — never correct that to the recommendation;
    // the size gate (custom-product.js) reapplies her pick on other products of the type.
    const manual = readTouchedSizes()[hint.dataset.mmSizeKey || ''];
    if (manual) return false;
    if (hit.radio.checked) return false;
    // A sold-out recommendation is still her size: preselect it (the bar then shows "Sold out" / Klaviyo's
    // "Notify me"); only a variant that does not exist (radio.disabled) is skipped.
    if (hit.radio.disabled) return false;
    if (preselectBlockedByOverlay(hit.radio)) return false;
    return true;
  };

  const fillLine = (hint, hit) => {
    const textEl = hint.querySelector('.custom-size-finder-hint__text');
    if (!textEl) return;

    const soldOut = hit.radio.disabled || hit.label.classList.contains('is-disabled');
    let template;
    if (soldOut) {
      template = hint.dataset.mmTextSoldout;
    } else if (hit.radio.checked) {
      template = hint.dataset.mmTextPreselected;
    } else {
      template = hint.dataset.mmTextNeutral;
    }

    const displaySize = hit.label.textContent.trim();
    textEl.textContent = (template || '').replace('{size}', displaySize);
  };

  // Size finder in the sheet's footer band: the entry without a recommendation, the restart with one.
  const syncStickyIntro = (sticky, hit) => {
    const hasFinder = !!findMainFinderModal();
    const prompt = sticky.querySelector('.custom-sticky-size__prompt');
    const restart = sticky.querySelector('.custom-sticky-size__restart');
    if (prompt) prompt.hidden = !!hit || !hasFinder;
    if (restart) restart.hidden = !hit || !hasFinder;
  };

  // Line above the size field: shown only while the shown size differs from an available recommendation (never
  // during a pending preselect). "Switch" hands the recommendation to the size gate as the customer's own pick.
  const syncMismatch = (sticky, hint, hit) => {
    const line = sticky.querySelector('.custom-sticky-size__mismatch');
    if (!line) return;

    const soldOut = !hit || hit.radio.disabled || hit.label.classList.contains('is-disabled');
    const shown = selectedSizeLabel(hint);
    const size = hit ? hit.label.textContent.trim() : '';
    line.hidden = soldOut || !shown || shown === norm(size) || isPreselectPending(hint, hit);
    if (line.hidden) return;

    const text = line.querySelector('.custom-sticky-size__mismatch-text');
    if (text) text.textContent = (line.dataset.customTextMismatch || '').replace('{size}', size);
    const button = line.querySelector('[data-custom-size-switch]');
    if (!button) return;
    button.setAttribute('aria-label', (line.dataset.customTextSwitchLabel || '').replace('{size}', size));
    button.setAttribute('data-custom-pick-form', hint.dataset.mmFormId || '');
    button.setAttribute('data-custom-pick-position', hit.radio.dataset.optionPosition || '');
    button.setAttribute('data-custom-pick-value', hit.radio.value);
  };

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-custom-size-switch]') : null;
    if (!button) return;
    const detail = {
      formId: button.getAttribute('data-custom-pick-form') || '',
      position: button.getAttribute('data-custom-pick-position') || '',
      value: button.getAttribute('data-custom-pick-value') || '',
    };
    if (!detail.formId || !detail.position || !detail.value) return;
    document.dispatchEvent(new CustomEvent('custom:size-pick', { detail }));
  });

  // Sticky bar: mirror the recommendation into the size sheet. Rows are matched by radio VALUE, not
  // label text; idempotent over every copy of the selector. Call after hint.hidden is final.
  const syncSticky = (hint, hit) => {
    const formId = hint.dataset.mmFormId || '';
    const position = hint.dataset.mmSizeOptionPosition || '';

    // Sync every copy of the selector for this form plus an OPEN sheet — the theme moves an open
    // x-popover to body, so it is no longer inside .custom-sticky-size while it is shown.
    const roots = new Set();
    document.querySelectorAll(`${STICKY_SELECTOR}[data-form-id="${formId}"]`).forEach((el) => roots.add(el));
    document.querySelectorAll(`${STICKY_SHEET_SELECTOR}[id$="${formId}"]`).forEach((el) => roots.add(el));

    roots.forEach((sticky) => {
      sticky.querySelectorAll('[data-mm-recommended]').forEach((row) => {
        row.removeAttribute('data-mm-recommended');
        const tag = row.querySelector('.custom-sticky-size__tag');
        if (tag) tag.hidden = true;
      });

      // No marker dot on the trigger button: the row tag, the footer band and the mismatch line carry the recommendation.
      syncStickyIntro(sticky, hit);
      syncMismatch(sticky, hint, hit);
      if (!hit) return;

      sticky.querySelectorAll(`input[data-option-position="${position}"]`).forEach((input) => {
        if (input.value !== hit.radio.value) return;
        const row = input.closest('.custom-sticky-size__option');
        if (!row) return;
        row.setAttribute('data-mm-recommended', '');
        const tag = row.querySelector('.custom-sticky-size__tag');
        if (tag) tag.hidden = false;
      });
    });
  };

  // Completed quiz = the size is known: hand the recommendation (sold out too) to the size gate (custom-product.js),
  // which counts it as picked unless the customer chose by hand. Idempotent, fires on every reconcile.
  const announceRecommendation = (hint, hit) => {
    if (hit.radio.disabled) return;
    if (readTouchedSizes()[hint.dataset.mmSizeKey || '']) return;
    document.dispatchEvent(new CustomEvent('custom:size-recommended', {
      detail: {
        formId: hint.dataset.mmFormId || '',
        position: hit.radio.dataset.optionPosition || '',
        value: hit.radio.value,
      },
    }));
  };

  // Idempotent: (re)set marker + line to match the current DOM/profile state. Never changes
  // the selection. Keeps the hint hidden while a preselect is still pending (no-flash rule).
  const reconcile = (hint) => {
    const picker = getPickerFor(hint);
    if (!picker) return;

    picker.querySelectorAll('.' + MARKER_CLASS).forEach((el) => el.classList.remove(MARKER_CLASS));

    const hit = findRecommended(hint, picker);
    if (!hit) {
      hint.hidden = true;
      syncSticky(hint, null);
      return;
    }

    hit.label.classList.add(MARKER_CLASS);
    announceRecommendation(hint, hit);

    // Preselect about to happen -> stay hidden; the post-rerender reconcile shows the final line.
    if (isPreselectPending(hint, hit)) {
      hint.hidden = true;
      syncSticky(hint, hit);
      return;
    }

    fillLine(hint, hit);
    hint.hidden = false;
    syncSticky(hint, hit);
  };

  const findSizeGuideTrigger = (picker) => picker.querySelector(SIZE_GUIDE_TRIGGER_SELECTOR);

  // Only some size guide pages contain the finder quiz (templates with an empty or legacy
  // size_chart_page do not), so the prompt must never open a popup that cannot answer it.
  const sizeGuideHasFinder = (trigger) => {
    const modalId = trigger.getAttribute('aria-controls');
    if (!modalId) return false;
    const modal = document.getElementById(modalId);
    return !!(modal && modal.querySelector('#mm-sizefinder'));
  };

  // The quick-buy copy of the size guide is dead (see capture listener below), so a prompt inside the
  // modal hands off to the main product's size-guide modal — or shows nothing when there is none.
  const findMainFinderModal = () => {
    const mainPicker = document.querySelector(PICKER_SELECTOR);
    const trigger = mainPicker && findSizeGuideTrigger(mainPicker);
    if (!trigger || !sizeGuideHasFinder(trigger)) return null;
    const modal = document.getElementById(trigger.getAttribute('aria-controls'));
    return modal && typeof modal.show === 'function' ? modal : null;
  };

  // Idempotent, mirrors reconcile(): the prompt is the fallback for "no recommendation to show".
  const reconcilePrompt = (prompt) => {
    const picker = getPickerFor(prompt);
    if (!picker) {
      prompt.hidden = true;
      return;
    }

    // A recommendation exists (or is about to be applied) -> the hint owns this line.
    if (findRecommended(prompt, picker)) {
      prompt.hidden = true;
      return;
    }

    if (prompt.closest('quick-buy-modal')) {
      prompt.hidden = !findMainFinderModal();
      return;
    }

    const trigger = findSizeGuideTrigger(picker);
    prompt.hidden = !(trigger && sizeGuideHasFinder(trigger));
  };

  // Auto-select the recommended size — only on init and after a fresh quiz result.
  const maybePreselect = (hint) => {
    const picker = getPickerFor(hint);
    if (!picker) return;

    const hit = findRecommended(hint, picker);
    if (!hit) return;

    const handle = hint.dataset.mmProductHandle || '';
    if (!isPreselectPending(hint, hit)) {
      // Already selected -> remember so we don't keep re-checking. Sold out/touched -> leave as is.
      if (hit.radio.checked) preselectedHandles.add(handle);
      return;
    }

    // Mark before clicking so the click-triggered rerender/reconnect cannot re-enter here.
    preselectedHandles.add(handle);
    hit.radio.click();
  };

  const apply = (hint) => {
    reconcile(hint);
    maybePreselect(hint);
  };

  const reconcileAll = () => requestAnimationFrame(() => {
    eachHint(reconcile);
    eachPrompt(reconcilePrompt);
  });
  const applyAll = () => requestAnimationFrame(() => {
    eachHint(apply);
    eachPrompt(reconcilePrompt);
  });

  // Clarity session tag for the size finder entry point, set synchronously on the click (before the
  // quiz's own tags fire on dialog:after-show). Tracking must never break the entry point.
  const tagEntry = (value) => {
    try {
      if (typeof window.clarity === 'function') window.clarity('set', 'sf_entry', value);
    } catch (error) {
      /* no tracking, no problem */
    }
  };

  // Form the finder was opened for (sheet, quick-buy prompt); the main product otherwise.
  let finderTargetFormId = '';

  const finderTargetHint = () => {
    const hints = Array.from(document.querySelectorAll(HINT_SELECTOR));
    return hints.find((hint) => finderTargetFormId && hint.dataset.mmFormId === finderTargetFormId)
      || hints.find((hint) => !hint.closest('quick-buy-modal'))
      || null;
  };

  // Result button in the modal: "Select size M" for the recommendation of this product (sold out too — selecting
  // it offers "Notify me"), hidden without one (the page link would leave the product page).
  const syncFinderCta = () => {
    const ctas = document.querySelectorAll(FINDER_CTA_SELECTOR);
    if (!ctas.length) return;

    const hint = finderTargetHint();
    const picker = hint && getPickerFor(hint);
    const hit = picker && findRecommended(hint, picker);
    const available = !!hit && !hit.radio.disabled;
    const focus = (hint && hint.dataset.mmSizeKey) || '';

    ctas.forEach((cta) => {
      // Size key of this product on the quiz root: the page content highlights that size.
      const finder = cta.closest('#mm-sizefinder');
      if (finder && focus) finder.setAttribute('data-mm-focus', focus);
      else if (finder) finder.removeAttribute('data-mm-focus');
      cta.hidden = !available;
      if (!available) return;
      cta.textContent = (cta.dataset.mmCtaSelect || '').replace('{size}', hit.label.textContent.trim());
      cta.setAttribute('data-custom-accept-form', hint.dataset.mmFormId || '');
      cta.setAttribute('data-custom-accept-position', hit.radio.dataset.optionPosition || '');
      cta.setAttribute('data-custom-accept-value', hit.radio.value);
    });
  };

  // Result button tapped in the modal: hand the size to the size gate (custom-product.js), which selects it
  // once the modal has closed. Capture phase, before the link navigates.
  document.addEventListener('click', (event) => {
    const cta = event.target instanceof Element ? event.target.closest(FINDER_CTA_SELECTOR) : null;
    if (!cta) return;
    event.preventDefault();

    const detail = {
      formId: cta.getAttribute('data-custom-accept-form') || '',
      position: cta.getAttribute('data-custom-accept-position') || '',
      value: cta.getAttribute('data-custom-accept-value') || '',
    };
    if (cta.hidden || !detail.formId || !detail.position || !detail.value) return;

    document.dispatchEvent(new CustomEvent('custom:size-accept', { detail }));
    try {
      if (typeof window.clarity === 'function') window.clarity('event', 'Size Finder: Select size');
    } catch (error) {
      /* no tracking, no problem */
    }
    cta.closest(FINDER_MODAL_SELECTOR)?.hide?.();
  }, true);

  // Delegated at document because the variant-picker block (and with it both the prompt and
  // the theme's size guide trigger) is replaced on every partial rerender.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // "Size finder" button in the sticky sheet: tag the entry and open the main product's size-guide
    // modal via modal.show() — a trigger.click() outside the sheet would trip its focus trap and close it.
    const stickyGuide = target.closest(STICKY_GUIDE_SELECTOR);
    if (stickyGuide) {
      const mainPicker = document.querySelector(PICKER_SELECTOR);
      const trigger = mainPicker && findSizeGuideTrigger(mainPicker);
      const modalId = trigger && trigger.getAttribute('aria-controls');
      const modal = modalId && document.getElementById(modalId);
      if (!modal || typeof modal.show !== 'function') return;

      const mode = stickyGuide.dataset.mmStickyGuide;
      const sheet = stickyGuide.closest(STICKY_SHEET_SELECTOR);
      finderTargetFormId = sheet ? sheet.id.replace('custom-sticky-size-popover-', '') : '';
      tagEntry(mode === 'prompt' ? 'sticky_prompt' : mode === 'restart' ? 'sticky_restart' : 'sticky_bar');
      modal.show();
      // Restart = the quiz's own "Start over" (delegated on #mm-sizefinder); the click stays inside the open modal.
      if (mode === 'restart') modal.querySelector('#mm-sizefinder [data-mm-action="restart"]')?.click();
      return;
    }

    if (target.closest('[data-mm-prompt-open]')) {
      const prompt = target.closest(PROMPT_SELECTOR);
      if (prompt && prompt.closest('quick-buy-modal')) {
        const modal = findMainFinderModal();
        if (!modal) return;
        finderTargetFormId = prompt.dataset.mmFormId || '';
        tagEntry('quick_buy_prompt');
        modal.show();
        return;
      }
      const picker = prompt && getPickerFor(prompt);
      const trigger = picker && findSizeGuideTrigger(picker);
      if (!trigger) return;
      finderTargetFormId = prompt.dataset.mmFormId || '';
      tagEntry('pdp_prompt');
      // Programmatic click -> isTrusted === false, so the handler below cannot overwrite the tag.
      trigger.click();
      return;
    }

    if (event.isTrusted && target.closest(SIZE_GUIDE_TRIGGER_SELECTOR)) {
      tagEntry('size_row');
    }
  });

  // Inside the quick-buy modal the embedded size-chart copy is dead (injected via DOMParser, quiz script
  // bound to the first #mm-sizefinder only): intercept in capture phase and open the MAIN size-chart modal.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest(SIZE_GUIDE_TRIGGER_SELECTOR);
    if (!trigger || !trigger.closest('quick-buy-modal')) return;

    const mainPicker = document.querySelector(PICKER_SELECTOR);
    const mainTrigger = mainPicker && findSizeGuideTrigger(mainPicker);
    const mainModalId = mainTrigger && mainTrigger.getAttribute('aria-controls');
    const mainModal = mainModalId && document.getElementById(mainModalId);
    if (!mainModal || typeof mainModal.show !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    tagEntry('quick_buy');
    mainModal.show();
  }, true);

  const boot = () => {
    lastProfileRaw = readProfileRaw();
    eachHint(apply);
    eachPrompt(reconcilePrompt);
  };

  // product:rerender does not bubble, but the capture phase still passes through document, so one
  // listener survives any form replacement (the quick-buy modal swaps its whole form per variant change).
  document.addEventListener('product:rerender', (event) => {
    const target = event.target;
    if (!target || typeof target.getAttribute !== 'function') return;
    const formId = target.getAttribute('id');
    if (!formId) return;
    let match = false;
    eachHint((hint) => {
      if (formId === hint.dataset.mmFormId) match = true;
    });
    if (match) reconcileAll();
  }, true);

  // variant:change bubbles from the product forms (main + quick buy); only react to forms with a hint.
  // Read the id via getAttribute — on the product form, form.id is shadowed by the <input name="id">.
  document.addEventListener('variant:change', (event) => {
    const target = event.target;
    if (!target || typeof target.getAttribute !== 'function') return;
    const formId = target.getAttribute('id');
    if (!formId) return;
    let match = false;
    eachHint((hint) => {
      if (formId === hint.dataset.mmFormId) match = true;
    });
    if (match) reconcileAll();
  });

  // Track a genuine manual size change. Programmatic radio.click() has isTrusted === false,
  // so our own preselect never marks the place as touched.
  document.addEventListener('change', (event) => {
    if (!event.isTrusted) return;
    const radio = event.target;
    if (!radio || radio.tagName !== 'INPUT' || !radio.dataset) return;
    const position = radio.dataset.optionPosition;
    if (!position) return;
    // Match on form association, not only the option position — main product and quick-buy share
    // the same size position, and a manual choice in one context must not lock the other.
    const radioFormId = radio.getAttribute('form') || '';
    eachHint((hint) => {
      if (position === hint.dataset.mmSizeOptionPosition && radioFormId === hint.dataset.mmFormId) {
        touchedScopes.add(scopeOf(hint));
        rememberTouchedSize(hint);
      }
    });
  }, true);

  // Fresh quiz result (size guide closed or mm:size-profile-updated fired): only re-preselect when
  // the profile actually changed, so merely viewing the size guide never overrides a manual choice.
  const handleProfileMaybeChanged = () => {
    const raw = readProfileRaw();
    if (raw === lastProfileRaw) {
      // Unchanged profile: still re-apply so a preselect blocked by an open overlay is caught up;
      // the guards keep this idempotent.
      applyAll();
      return;
    }
    lastProfileRaw = raw;
    preselectedHandles.clear();
    touchedScopes.clear();
    forgetTouchedSizes();
    applyAll();
  };

  // Quick-buy modal: content is injected fresh on every open; dialog:before-show does not bubble,
  // so catch it in capture phase and apply marker/preselect/line to the modal's hints.
  document.addEventListener('dialog:before-show', (event) => {
    const modal = event.target;
    if (!(modal instanceof Element) || !modal.matches('quick-buy-modal')) return;
    requestAnimationFrame(() => {
      const handles = [];
      modal.querySelectorAll(HINT_SELECTOR).forEach((hint) => {
        handles.push(hint.dataset.mmProductHandle || '');
        apply(hint);
      });
      modal.querySelectorAll(PROMPT_SELECTOR).forEach(reconcilePrompt);
      modal.__customSizeFinderHandles = handles;
    });
  }, true);

  // The size finder quiz lives in the size guide popup (x-modal[id^="size-chart-"]); dialog:after-hide
  // fires on every close path but does not bubble, so catch it in capture phase at document.
  document.addEventListener('dialog:after-hide', (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    if (el.matches(FINDER_MODAL_SELECTOR)) {
      finderTargetFormId = '';
      handleProfileMaybeChanged();
      return;
    }
    // Sticky size sheet closed -> re-apply so a preselect blocked while the sheet was open is caught
    // up; the guards keep a manual sheet choice untouched.
    if (el.matches(STICKY_SHEET_SELECTOR)) {
      applyAll();
      return;
    }
    // Quick-buy closed -> release the one-shot guards for the modal's product and for the modal as a
    // place (its content is rebuilt on the next open), then catch up a blocked main-product preselect.
    if (el.matches('quick-buy-modal')) {
      touchedScopes.delete(`quick-buy:${el.id}`);

      if (Array.isArray(el.__customSizeFinderHandles)) {
        el.__customSizeFinderHandles.forEach((handle) => preselectedHandles.delete(handle));
        el.__customSizeFinderHandles = null;
      }
      applyAll();
    }
  }, true);

  // Result button follows the modal and every new quiz result.
  document.addEventListener('dialog:after-show', (event) => {
    if (event.target instanceof Element && event.target.matches(FINDER_MODAL_SELECTOR)) syncFinderCta();
  }, true);
  window.addEventListener('mm:size-profile-updated', () => requestAnimationFrame(syncFinderCta));

  window.addEventListener('mm:size-profile-updated', handleProfileMaybeChanged);
  document.addEventListener('mm:size-profile-updated', handleProfileMaybeChanged);

  // Cross-tab profile change -> reconcile only (no surprising selection change).
  window.addEventListener('storage', (event) => {
    if (!event || event.key === PROFILE_KEY) reconcileAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
